import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Search, Loader2, LifeBuoy, MessageSquare } from 'lucide-react';
import TicketDialog, { STATUS_META, PRIORITY_META, TYPE_LABEL, formatDate } from '@/components/TicketDialog';

/** Coordinator view: tickets raised by this school, read-only. */
export default function RequestsAndIssues() {
  const [tickets, setTickets] = useState(null);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (status !== 'all') params.status = status;
    if (search.trim()) params.search = search.trim();
    try {
      const res = await api.get('/tickets', { params });
      setTickets(res.data);
    } catch {
      setTickets([]);
    }
  }, [status, search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  useEffect(() => {
    window.addEventListener('ticket-created', load);
    return () => window.removeEventListener('ticket-created', load);
  }, [load]);

  return (
    <div className="space-y-4" data-testid="coordinator-tickets">
      <p className="text-sm text-[#6B7280]">
        Everything your school has raised through the Report Issue button, with where it stands and
        what we have said about it. Use the button at the bottom left of any page to raise a new one.
      </p>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ticket number or subject"
                 className="pl-9 rounded-xl bg-white" data-testid="coordinator-ticket-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-48 rounded-xl bg-white" data-testid="coordinator-ticket-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_META).map(([v, m]) => (
              <SelectItem key={v} value={v} data-testid={`coordinator-filter-${v}`}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {tickets === null ? (
        <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#F97D6A]" /></div>
      ) : tickets.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center" data-testid="coordinator-tickets-empty">
          <LifeBuoy className="h-8 w-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-[#6B7280]">Nothing raised yet. Use the Report Issue button at the bottom left of any page.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FA] text-[10px] uppercase tracking-widest text-gray-400">
              <tr>
                <th className="text-left py-3 px-4 font-bold">Ticket</th>
                <th className="text-left py-3 px-4 font-bold">Subject</th>
                <th className="text-left py-3 px-4 font-bold hidden sm:table-cell">Type</th>
                <th className="text-left py-3 px-4 font-bold">Status</th>
                <th className="text-left py-3 px-4 font-bold hidden md:table-cell">Priority</th>
                <th className="text-left py-3 px-4 font-bold hidden lg:table-cell">Raised</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map(t => (
                <tr key={t.id} onClick={() => setOpenId(t.id)}
                    className="border-t border-gray-100 hover:bg-[#FFF8F7] cursor-pointer transition-colors"
                    data-testid={`coordinator-ticket-row-${t.school_ticket_number || t.ticket_number}`}>
                  <td className="py-3 px-4 font-mono text-xs font-semibold text-[#1F2937]">{t.school_ticket_number || t.ticket_number}</td>
                  <td className="py-3 px-4 text-[#1F2937] max-w-xs break-words">
                    {t.subject}
                    {t.note_count > 0 && (
                      <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-[#6B7280] align-middle" data-testid={`coordinator-ticket-notes-${t.school_ticket_number || t.ticket_number}`}>
                        <MessageSquare className="h-3 w-3" />{t.note_count}
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-[#6B7280] hidden sm:table-cell">{TYPE_LABEL[t.type]}</td>
                  <td className="py-3 px-4"><Badge className={`${STATUS_META[t.status].cls} border-0 text-[10px]`}>{STATUS_META[t.status].label}</Badge></td>
                  <td className="py-3 px-4 hidden md:table-cell"><Badge className={`${PRIORITY_META[t.priority].cls} border-0 text-[10px]`}>{PRIORITY_META[t.priority].label}</Badge></td>
                  <td className="py-3 px-4 text-xs text-[#9CA3AF] hidden lg:table-cell">{formatDate(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId && <TicketDialog ticketId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
